<template>
  <ConfirmModal persist wrapper-class="w-[100%] max-w-xl" data-testid="prospects-edit-list-modal" @close-dialog="handleForm">
    <template #title>
      <h1 class="p-6 pb-5 text-headline-m">{{ t('ProspectsCatalog.editList') }}</h1>
    </template>
    <template #content>
      <div class="p-6">
        <ProspectListForm ref="formRef" :prospect-list="activeList" :event="activeList.event" @submit="handleSubmit" />
      </div>
    </template>
    <template #actions>
      <div class="ml-6 flex items-center">
        <button
          v-if="!activeList.submittedAt"
          class="btn-secondary-destructive-s"
          data-testid="prospects-edit-delete"
          @click="goToDeleteModal"
        >
          <SvgIcon :path="mdiDelete" :size="28" class="mr-2 -ml-1" />
          {{ t('ProspectsCatalog.createListModal.delete') }}
        </button>
        <div class="mt-6 flex justify-end px-6 pb-6">
          <button class="btn-secondary-color-s uppercase" data-testid="prospects-edit-cancel" @click="handleForm(false)">
            {{ t('cancel') }}
          </button>
          <button class="btn-primary-color-s ml-4" data-testid="prospects-edit-save" @click="handleForm(true)">
            {{ t('ProspectsCatalog.updateListButton') }}
          </button>
        </div>
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { mdiDelete } from '@mdi/js'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { type MeQuery, useUpdateProspectListMutation } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { showNotification } from '@/helpers/notificationHelper'

import ProspectListForm from './ProspectListForm.vue'
import type { ProspectList } from './types'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()

const { mutate: updateProspectListMutation } = useUpdateProspectListMutation()

const formRef = ref()

const props = defineProps<{
  me: NonNullable<MeQuery['me']>
  activeList: ProspectList
}>()

const emit = defineEmits<{
  (e: 'refetchProspectLists'): void
}>()

function goToDeleteModal() {
  const activeListId = toPlainId(props.activeList.id)
  return router.push({ name: 'deleteProspectList', params: { listId: activeListId as string } })
}

function closeModal() {
  router.push({ name: 'prospectList', params: { listId: route.params.listId } })
}

async function handleForm(shouldSubmit: boolean) {
  if (!shouldSubmit) return closeModal()

  formRef.value.submit()
}

async function handleSubmit(formData: { listName: string; contactName: string; contactEmail: string; contactReason: string }) {
  try {
    await updateProspectListMutation({
      prospectListId: props.activeList.id,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      contactReason: formData.contactReason,
      title: formData.listName,
    })
    showNotification(t('ProspectsCatalog.createListModal.successfulNotification'), 'success', 3000)
    emit('refetchProspectLists')
    closeModal()
  } catch (_e) {
    showNotification(t('ProspectsCatalog.createListModal.failedNotification'), 'error', 3000)
    emit('refetchProspectLists')
    closeModal()
  }
}
</script>
